import { daySeconds, type DayRow, type Family } from "../../shared/model";
import { addDays, dayKey } from "./clock";
import type { LocalData } from "./store";

export interface DayTotals {
  total: number;
  cut: number;
  stars: number;
  coin: number;
  solved: number;
}

const empty = (): DayTotals => ({ total: 0, cut: 0, stars: 0, coin: 0, solved: 0 });

function add(t: DayTotals, r: DayRow): void {
  t.cut += r.cut;
  t.stars += r.stars;
  t.coin += r.coin;
  t.solved += r.solved;
  t.total = t.cut + t.stars + t.coin;
}

/** this device plus every other device */
export function dayTotals(d: LocalData, day: string): DayTotals {
  const t = empty();
  const own = d.days[day];
  if (own) add(t, own);
  for (const row of Object.values(d.others[day] ?? {})) add(t, row);
  return t;
}

export function allDays(d: LocalData): string[] {
  return [...new Set([...Object.keys(d.days), ...Object.keys(d.others)])].sort();
}

export interface Streak {
  current: number;
  best: number;
}

/** consecutive days on which the daily goal was met; today doesn't break the streak until it is over */
export function streak(d: LocalData, today: string = dayKey()): Streak {
  const goal = d.settings.goalMinutes * 60;
  const met = (day: string) => dayTotals(d, day).total >= goal;
  let current = 0;
  let cursor = met(today) ? today : addDays(today, -1);
  while (met(cursor)) {
    current++;
    cursor = addDays(cursor, -1);
  }
  let best = 0;
  let run = 0;
  let prev: string | null = null;
  for (const day of allDays(d)) {
    if (!met(day)) continue;
    run = prev && addDays(prev, 1) === day ? run + 1 : 1;
    best = Math.max(best, run);
    prev = day;
  }
  return { current, best: Math.max(best, current) };
}

export function totals(d: LocalData): { seconds: number; days: number; solved: number; byFamily: Record<Family, number> } {
  const byFamily: Record<Family, number> = { cut: 0, stars: 0, coin: 0 };
  let seconds = 0;
  let days = 0;
  for (const day of allDays(d)) {
    const t = dayTotals(d, day);
    seconds += t.total;
    if (t.total >= 60) days++;
    byFamily.cut += t.cut;
    byFamily.stars += t.stars;
    byFamily.coin += t.coin;
  }
  const solved = Object.values(d.progress).filter((p) => p.status === "solved").length;
  return { seconds, days, solved, byFamily };
}

/** a grid of weeks (Monday first) ending with the current week, for the calendar heat map */
export function calendar(d: LocalData, weeks: number, today: string = dayKey()): { day: string; secs: number; future: boolean }[][] {
  const t = new Date(today + "T12:00:00");
  const mondayOffset = (t.getDay() + 6) % 7;
  const start = addDays(today, -mondayOffset - (weeks - 1) * 7);
  const out: { day: string; secs: number; future: boolean }[][] = [];
  for (let w = 0; w < weeks; w++) {
    const col: { day: string; secs: number; future: boolean }[] = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(start, w * 7 + i);
      col.push({ day, secs: dayTotals(d, day).total, future: day > today });
    }
    out.push(col);
  }
  return out;
}

export { daySeconds };
